(() => {
  const WRAPPER_SELECTOR = '.custom_upsells_wrapper';
  const productMap = new Map();
  let observerStarted = false;
  let globalEventsBound = false;

  /* -------------------------------------------
     BOOT / INIT
  ------------------------------------------- */
  function boot() {
    startObserver();
    bindGlobalEvents();
    initExistingWrappers();
  }

  function startObserver() {
    if (observerStarted) return;
    observerStarted = true;

    const obs = new MutationObserver(() => {
      initExistingWrappers();
    });

    obs.observe(document.documentElement, { childList: true, subtree: true });
  }

  function bindGlobalEvents() {
    if (globalEventsBound) return;
    globalEventsBound = true;

    document.addEventListener('cart:refresh', debounceInitExistingWrappers, true);
    document.addEventListener('cart:updated', debounceInitExistingWrappers, true);
    document.addEventListener('shopify:section:load', debounceInitExistingWrappers, true);
    document.addEventListener('shopify:section:reorder', debounceInitExistingWrappers, true);
    document.addEventListener('shopify:section:select', debounceInitExistingWrappers, true);
    document.addEventListener('shopify:section:deselect', debounceInitExistingWrappers, true);
    window.addEventListener('pageshow', debounceInitExistingWrappers);
  }

  function debounceInitExistingWrappers() {
    clearTimeout(window.__customUpsellsInitTimer);
    window.__customUpsellsInitTimer = setTimeout(() => {
      initExistingWrappers();
    }, 80);
  }

  function initExistingWrappers() {
    document.querySelectorAll(WRAPPER_SELECTOR).forEach(wrapper => {
      if (!wrapper.dataset.upsellsInit) initUpsells(wrapper);
    });
  }

  function initUpsells(wrapper) {
    wrapper.dataset.upsellsInit = "1";
    const url = wrapper.getAttribute('data-reco-url');
    if (!url) return;

    wrapper.innerHTML = `<div class="custom_upsells_loading">Loading...</div>`;

    fetch(url, { credentials: 'same-origin' })
      .then(r => r.json())
      .then(data => {
        const products = data?.products || [];
        if (!products.length) { wrapper.innerHTML = ''; return; }

        wrapper.innerHTML = `
          <div class="custom_upsells_section">
            <h4 class="cart-upsells__heading t-mt-0 t-mb-4 h4 t-text-center">You may also like</h4>

            <div class="swiper custom-upsells-swiper">
              <div class="swiper-wrapper"></div>
            </div>
          </div>
        `;

        const swiperEl = wrapper.querySelector('.custom-upsells-swiper');
        const track = swiperEl.querySelector('.swiper-wrapper');

        products.forEach(p => {
          if ((p.product_type || '').toLowerCase() === 'bundle') return;
          if (!p.handle || !p.variants?.length) return;

          productMap.set(p.handle, p);

          const baseImg = p.featured_image || p.images?.[0] || '';
          const title = escapeHtml(p.title || '');
          const productUrl = `/products/${p.handle}`;

          track.insertAdjacentHTML('beforeend', `
            <div class="swiper-slide">
              <div class="custom_upsell_card" data-handle="${p.handle}" data-selected="{}">
                <div class="custom_upsell_top">
                <span class="custom_upsell_img">
                    ${baseImg ? `<img class="custom_upsell_img_tag" src="${baseImg}" alt="${title}" loading="lazy">` : ``}
                </span>

                  <div>
                    <div class="custom_upsell_headline">
                      <small class="t-font-medium">${title}</small>
                      <div class="custom_upsell_price"></div>
                    </div>

                    <div class="options_wrapper">
                    <div class="custom_upsell_selected">Select options</div>

                    <div class="custom_upsell_headline">
                      <span></span>
                      <span class="custom_upsell_edit" role="button" tabindex="0">Edit</span>
                    </div>
                    </div>
                  </div>
                </div>

                <div class="custom_upsell_options"></div>

                <button class="custom_upsell_add" type="button" disabled data-variant-id="">
                  ADD
                </button>
              </div>
            </div>
          `);
        });

        if (!track.children.length) { wrapper.innerHTML = ''; return; }

        wrapper.querySelectorAll('.custom_upsell_card').forEach(card => {
          buildOptionPickers(card);
          autoSelectFirstAvailable(card);
        });

        initSwiper(swiperEl);
        attachInteractions(wrapper);
      })
      .catch(err => {
        console.error('Upsells error:', err);
        wrapper.innerHTML = '';
      });
  }

  /* -------------------------------------------
     SWIPER: 1.5 slides, no center
  ------------------------------------------- */
  function initSwiper(swiperEl) {
    if (!swiperEl || !window.Swiper) return;

    if (swiperEl.swiper) {
      try { swiperEl.swiper.destroy(true, true); } catch(e){}
    }

    new Swiper(swiperEl, {
      slidesPerView: 1.5,
      slidesPerGroup: 1,
      centeredSlides: false,
      spaceBetween: 12,
      freeMode: false,
      speed: 350,
      preventClicks: false,
      preventClicksPropagation: false,
      touchStartPreventDefault: false,
      observer: true,
      observeParents: true,
      watchOverflow: true
    });
  }

  /* -------------------------------------------
     BUILD OPTION PICKERS
  ------------------------------------------- */
  function buildOptionPickers(card) {
    const handle = card.dataset.handle;
    const p = productMap.get(handle);
    if (!p) return;

    const optsEl = card.querySelector('.custom_upsell_options');
    optsEl.innerHTML = '';

    const variants = p.variants || [];
    const optionNames = normalizeOptionNames(p);

    if (variants.length === 1) {
      const edit = card.querySelector('.custom_upsell_edit');
      if (edit) edit.style.display = 'none';
      return;
    }

    [1, 2, 3].forEach(idx => {
      const key = `option${idx}`;
      const values = [...new Set(variants.map(v => v[key]).filter(Boolean))];
      if (!values.length) return;

      const name = optionNames[idx - 1] || `Option ${idx}`;
      const isColor = /color|colour/i.test(name);

      optsEl.insertAdjacentHTML('beforeend', `
        <div class="upsell_option" data-option-index="${idx}">
          <div class="upsell_option_label">${escapeHtml(name)}</div>
          <div class="upsell_option_values">
            ${values.map(rawVal => {
              const label = escapeHtml(rawVal);
              const encoded = encodeURIComponent(rawVal);

              if (isColor) {
                const Value_ = rawVal.toLowerCase().replace(/\s+/g, '');
                const swatchImg_ = `https://myntlab.com/cdn/shop/files/${Value_}fabricswatch.jpg?width=60`;

                return `
                  <button type="button"
                    class="upsell_opt_btn is-swatch-img"
                    data-value="${encoded}"
                    title="${label}"
                    style="background-image:url('${swatchImg_}')">
                    <span class="visually-hidden">${label}</span>
                  </button>
                `;
              }

              return `
                <button type="button"
                  class="upsell_opt_btn"
                  data-value="${encoded}">${label}</button>
              `;
            }).join('')}
          </div>
        </div>
      `);
    });

    if (!optsEl.children.length) {
      const edit = card.querySelector('.custom_upsell_edit');
      if (edit) edit.style.display = 'none';
    }
  }

  /* -------------------------------------------
     AUTO SELECT FIRST AVAILABLE VARIANT
  ------------------------------------------- */
  function autoSelectFirstAvailable(card) {
    const handle = card.dataset.handle;
    const p = productMap.get(handle);
    if (!p) return;

    const variants = p.variants || [];
    const addBtn = card.querySelector('.custom_upsell_add');
    const priceEl = card.querySelector('.custom_upsell_price');
    const selectedEl = card.querySelector('.custom_upsell_selected');

    const firstAvail = variants.find(v => v.available) || variants[0];
    if (!firstAvail) return;

    if (variants.length === 1) {
      addBtn.disabled = !firstAvail.available;
      addBtn.dataset.variantId = firstAvail.id;
      if (priceEl) priceEl.textContent = formatMoney(firstAvail.price);
      setCardImage(card, p, firstAvail);
      if (selectedEl) selectedEl.textContent = firstAvail.available ? 'Ready to add' : 'Sold out';
      return;
    }

    const selected = {};
    if (firstAvail.option1) selected.option1 = firstAvail.option1;
    if (firstAvail.option2) selected.option2 = firstAvail.option2;
    if (firstAvail.option3) selected.option3 = firstAvail.option3;

    card.dataset.selected = JSON.stringify(selected);

    Object.keys(selected).forEach(k => {
      const idx = Number(k.replace('option', ''));
      const val = selected[k];

      const wrap = card.querySelector(`.upsell_option[data-option-index="${idx}"]`);
      if (!wrap) return;

      wrap.querySelectorAll('.upsell_opt_btn').forEach(b => b.classList.remove('is-active'));

      const btn = [...wrap.querySelectorAll('.upsell_opt_btn')]
        .find(b => decodeURIComponent(b.dataset.value || '') === val);

      if (btn) btn.classList.add('is-active');
    });

    updateVariantForCard(card);
  }

  /* -------------------------------------------
     INTERACTIONS
  ------------------------------------------- */
  function attachInteractions(wrapper) {
    if (wrapper.dataset.upsellsBound === "1") return;
    wrapper.dataset.upsellsBound = "1";

    wrapper.addEventListener('click', async (e) => {
      const edit = e.target.closest('.custom_upsell_edit');
      if (edit) {
        const card = edit.closest('.custom_upsell_card');
        if (!card) return;

        card.classList.toggle('is-editing');
        edit.textContent = card.classList.contains('is-editing') ? 'Close' : 'Edit';
        return;
      }

      const optBtn = e.target.closest('.upsell_opt_btn');
      if (optBtn) {
        const card = optBtn.closest('.custom_upsell_card');
        const optWrap = optBtn.closest('.upsell_option');
        if (!card || !optWrap) return;

        optWrap.querySelectorAll('.upsell_opt_btn').forEach(b => b.classList.remove('is-active'));
        optBtn.classList.add('is-active');

        const idx = Number(optWrap.dataset.optionIndex);
        const rawVal = decodeURIComponent(optBtn.dataset.value || '');

        const selected = safeParse(card.dataset.selected, {});
        selected[`option${idx}`] = rawVal;
        card.dataset.selected = JSON.stringify(selected);

        updateVariantForCard(card);
        return;
      }

      const addBtn = e.target.closest('.custom_upsell_add');
      if (addBtn) {
        const variantId = addBtn.dataset.variantId;
        if (!variantId) return;

        addBtn.disabled = true;

        try {
          await addUpsellItemToCart(variantId, addBtn);
        } catch (err) {
          console.error('Upsell add error:', err);
        } finally {
          addBtn.disabled = false;
        }

        return;
      }
    }, true);
  }

  /* -------------------------------------------
     THEME-LIKE ADD TO CART FOR DRAWER
  ------------------------------------------- */
  async function addUpsellItemToCart(variantId, triggerEl) {
    const cartDrawer = document.querySelector('cart-drawer');
    const body = buildAddToCartBody(variantId);

    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      },
      body
    });

    const responseData = await response.json();

    if (responseData.status) {
      throw new Error(responseData.description || responseData.message || 'Unable to add product');
    }

    if (cartDrawer) {
      if (typeof cartDrawer.setActiveElement === 'function' && triggerEl) {
        cartDrawer.setActiveElement(triggerEl);
      }

      if (typeof cartDrawer.renderContents === 'function' && responseData.sections) {
        cartDrawer.renderContents(responseData);
      } else {
        await refreshCartUI();
      }

      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else {
        cartDrawer.classList.add('active');
      }
    } else {
      await refreshCartUI();
    }

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    window.dispatchEvent(new CustomEvent('cart:refresh'));
    window.dispatchEvent(new CustomEvent('cart:updated'));

    setTimeout(() => {
      initExistingWrappers();
    }, 120);
  }

  function buildAddToCartBody(variantId) {
    const cartDrawer = document.querySelector('cart-drawer');
    const formData = new FormData();

    formData.append('id', Number(variantId));
    formData.append('quantity', 1);

    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      const sections = cartDrawer.getSectionsToRender().map(section => section.id);
      formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);
    } else {
      const fallbackSections = getFallbackCartSectionIds();
      if (fallbackSections.length) {
        formData.append('sections', fallbackSections.join(','));
        formData.append('sections_url', window.location.pathname);
      }
    }

    return formData;
  }

  function getFallbackCartSectionIds() {
    return [...new Set(
      [...document.querySelectorAll('[id^="shopify-section-"]')]
        .map(el => el.id.replace('shopify-section-', ''))
        .filter(id => /cart|drawer|bubble|icon/i.test(id))
    )];
  }

  /* -------------------------------------------
     REFRESH CART UI AFTER UPSELL ADD
  ------------------------------------------- */
  async function refreshCartUI() {
    try {
      await refreshCartSections();
    } catch (e) {
      console.warn('Cart sections refresh fallback:', e);
    }

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    window.dispatchEvent(new CustomEvent('cart:refresh'));
    window.dispatchEvent(new CustomEvent('cart:updated'));

    setTimeout(() => {
      initExistingWrappers();
    }, 120);
  }

  async function refreshCartSections() {
    const sectionEls = [...document.querySelectorAll('[id^="shopify-section-"]')];
    const sectionIds = [...new Set(
      sectionEls
        .map(el => el.id.replace('shopify-section-', ''))
        .filter(id => /cart|drawer|bubble|icon/i.test(id))
    )];

    if (!sectionIds.length) return;

    const url = `${window.location.pathname}?sections=${encodeURIComponent(sectionIds.join(','))}`;
    const res = await fetch(url, { credentials: 'same-origin' });
    const htmlBySection = await res.json();

    sectionIds.forEach(sectionId => {
      const mount = document.getElementById(`shopify-section-${sectionId}`);
      const newHtml = htmlBySection[sectionId];

      if (mount && newHtml) {
        mount.innerHTML = newHtml;
      }
    });
  }

  /* -------------------------------------------
     VARIANT MATCH + PRICE + IMAGE UPDATE
  ------------------------------------------- */
  function updateVariantForCard(card) {
    const handle = card.dataset.handle;
    const p = productMap.get(handle);
    if (!p) return;

    const variants = p.variants || [];
    const selected = safeParse(card.dataset.selected, {});
    const addBtn = card.querySelector('.custom_upsell_add');
    const priceEl = card.querySelector('.custom_upsell_price');
    const selectedEl = card.querySelector('.custom_upsell_selected');

    if (variants.length === 1) {
      addBtn.disabled = !variants[0].available;
      addBtn.dataset.variantId = variants[0].id;
      if (priceEl) priceEl.textContent = formatMoney(variants[0].price);
      setCardImage(card, p, variants[0]);
      if (selectedEl) selectedEl.textContent = variants[0].available ? 'Ready to add' : 'Sold out';
      return;
    }

    const needed = [1,2,3].filter(i => variants.some(v => v[`option${i}`])).map(i => `option${i}`);
    const missing = needed.filter(k => !selected[k]);

    if (missing.length) {
      addBtn.disabled = true;
      addBtn.dataset.variantId = '';
      if (selectedEl) selectedEl.textContent = 'Select options';
      return;
    }

    const match = variants.find(v => needed.every(k =>
      String(v[k]).toLowerCase() === String(selected[k]).toLowerCase()
    ));

    if (!match) {
      addBtn.disabled = true;
      addBtn.dataset.variantId = '';
      if (selectedEl) selectedEl.textContent = 'Unavailable';
      return;
    }

    addBtn.dataset.variantId = match.id;
    if (priceEl) priceEl.textContent = formatMoney(match.price);
    setCardImage(card, p, match);

    if (!match.available) {
      addBtn.disabled = true;
      if (selectedEl) selectedEl.textContent = 'Sold out';
      return;
    }

    addBtn.disabled = false;
    if (selectedEl) selectedEl.textContent = needed.map(k => selected[k]).join(' / ');
  }

  function setCardImage(card, product, variant) {
    const imgTag = card.querySelector('.custom_upsell_img_tag');
    if (!imgTag) return;

    const src =
      variant?.featured_image?.src ||
      variant?.featured_image ||
      variant?.image ||
      product?.featured_image ||
      product?.images?.[0] ||
      '';

    if (src) imgTag.src = src;
  }

  /* -------------------------------------------
     HELPERS
  ------------------------------------------- */
  function normalizeOptionNames(p) {
    const o = p.options || [];
    if (Array.isArray(o) && o.length && typeof o[0] === 'string') return o;
    if (Array.isArray(o) && o.length && typeof o[0] === 'object') return o.map(x => x.name).filter(Boolean);
    return [];
  }

  function safeParse(json, fallback) {
    try { return JSON.parse(json); }
    catch (e) { return fallback; }
  }

  function formatMoney(cents) {
    const n = Number(cents);
    const currency = (window.Shopify?.currency?.active) ? Shopify.currency.active : 'AUD';
    try {
      return (n / 100).toLocaleString(undefined, { style: 'currency', currency });
    } catch (e) {
      return '$' + (n / 100).toFixed(2);
    }
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, m => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    }[m]));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();















// (() => {
//   const WRAPPER_SELECTOR = '.custom_upsells_wrapper';
//   const productMap = new Map();
//   let observerStarted = false;
//   let globalEventsBound = false;

//   /* -------------------------------------------
//      BOOT / INIT
//   ------------------------------------------- */
//   function boot() {
//     startObserver();
//     bindGlobalEvents();
//     initExistingWrappers();
//   }

//   function startObserver() {
//     if (observerStarted) return;
//     observerStarted = true;

//     const obs = new MutationObserver(() => {
//       initExistingWrappers();
//     });

//     obs.observe(document.documentElement, { childList: true, subtree: true });
//   }

//   function bindGlobalEvents() {
//     if (globalEventsBound) return;
//     globalEventsBound = true;

//     document.addEventListener('cart:refresh', debounceInitExistingWrappers, true);
//     document.addEventListener('cart:updated', debounceInitExistingWrappers, true);
//     document.addEventListener('shopify:section:load', debounceInitExistingWrappers, true);
//     document.addEventListener('shopify:section:reorder', debounceInitExistingWrappers, true);
//     document.addEventListener('shopify:section:select', debounceInitExistingWrappers, true);
//     document.addEventListener('shopify:section:deselect', debounceInitExistingWrappers, true);
//     window.addEventListener('pageshow', debounceInitExistingWrappers);
//   }

//   function debounceInitExistingWrappers() {
//     clearTimeout(window.__customUpsellsInitTimer);
//     window.__customUpsellsInitTimer = setTimeout(() => {
//       initExistingWrappers();
//     }, 80);
//   }

//   function initExistingWrappers() {
//     document.querySelectorAll(WRAPPER_SELECTOR).forEach(wrapper => {
//       if (!wrapper.dataset.upsellsInit) initUpsells(wrapper);
//     });
//   }

//   function initUpsells(wrapper) {
//     wrapper.dataset.upsellsInit = "1";
//     const url = wrapper.getAttribute('data-reco-url');
//     if (!url) return;

//     wrapper.innerHTML = `<div class="custom_upsells_loading">Loading...</div>`;

//     fetch(url, { credentials: 'same-origin' })
//       .then(r => r.json())
//       .then(data => {
//         const products = data?.products || [];
//         if (!products.length) { wrapper.innerHTML = ''; return; }

//         wrapper.innerHTML = `
//           <div class="custom_upsells_section">
//             <h4 class="cart-upsells__heading t-mt-0 t-mb-4 h4 t-text-center">You may also like</h4>

//             <div class="swiper custom-upsells-swiper">
//               <div class="swiper-wrapper"></div>
//             </div>
//           </div>
//         `;

//         const swiperEl = wrapper.querySelector('.custom-upsells-swiper');
//         const track = swiperEl.querySelector('.swiper-wrapper');

//         products.forEach(p => {
//           if ((p.product_type || '').toLowerCase() === 'bundle') return;
//           if (!p.handle || !p.variants?.length) return;

//           productMap.set(p.handle, p);

//           const baseImg = p.featured_image || p.images?.[0] || '';
//           const title = escapeHtml(p.title || '');
//           const productUrl = `/products/${p.handle}`;

//           track.insertAdjacentHTML('beforeend', `
//             <div class="swiper-slide">
//               <div class="custom_upsell_card" data-handle="${p.handle}" data-selected="{}">
//                 <div class="custom_upsell_top">
//                   <a class="custom_upsell_img" href="${productUrl}">
//                     ${baseImg ? `<img class="custom_upsell_img_tag" src="${baseImg}" alt="${title}" loading="lazy">` : ``}
//                   </a>

//                   <div>
//                     <div class="custom_upsell_headline">
//                       <small class="t-font-medium">${title}</small>
//                       <div class="custom_upsell_price"></div>
//                     </div>

//                     <div class="custom_upsell_selected">Select options</div>

//                     <div class="custom_upsell_headline" style="margin-top:-6px;">
//                       <span></span>
//                       <span class="custom_upsell_edit" role="button" tabindex="0">Edit</span>
//                     </div>
//                   </div>
//                 </div>

//                 <div class="custom_upsell_options"></div>

//                 <button class="custom_upsell_add" type="button" disabled data-variant-id="">
//                   ADD
//                 </button>
//               </div>
//             </div>
//           `);
//         });

//         if (!track.children.length) { wrapper.innerHTML = ''; return; }

//         wrapper.querySelectorAll('.custom_upsell_card').forEach(card => {
//           buildOptionPickers(card);
//           autoSelectFirstAvailable(card);
//         });

//         initSwiper(swiperEl);
//         attachInteractions(wrapper);
//       })
//       .catch(err => {
//         console.error('Upsells error:', err);
//         wrapper.innerHTML = '';
//       });
//   }

//   /* -------------------------------------------
//      SWIPER: 1.5 slides, no center
//   ------------------------------------------- */
//   function initSwiper(swiperEl) {
//     if (!swiperEl || !window.Swiper) return;

//     if (swiperEl.swiper) {
//       try { swiperEl.swiper.destroy(true, true); } catch(e){}
//     }

//     new Swiper(swiperEl, {
//       slidesPerView: 1.5,
//       slidesPerGroup: 1,
//       centeredSlides: false,
//       spaceBetween: 12,
//       freeMode: false,
//       speed: 350,
//       preventClicks: false,
//       preventClicksPropagation: false,
//       touchStartPreventDefault: false,
//       observer: true,
//       observeParents: true,
//       watchOverflow: true
//     });
//   }

//   /* -------------------------------------------
//      BUILD OPTION PICKERS
//   ------------------------------------------- */
//   function buildOptionPickers(card) {
//     const handle = card.dataset.handle;
//     const p = productMap.get(handle);
//     if (!p) return;

//     const optsEl = card.querySelector('.custom_upsell_options');
//     optsEl.innerHTML = '';

//     const variants = p.variants || [];
//     const optionNames = normalizeOptionNames(p);

//     if (variants.length === 1) {
//       const edit = card.querySelector('.custom_upsell_edit');
//       if (edit) edit.style.display = 'none';
//       return;
//     }

//     [1, 2, 3].forEach(idx => {
//       const key = `option${idx}`;
//       const values = [...new Set(variants.map(v => v[key]).filter(Boolean))];
//       if (!values.length) return;

//       const name = optionNames[idx - 1] || `Option ${idx}`;
//       const isColor = /color|colour/i.test(name);

//       optsEl.insertAdjacentHTML('beforeend', `
//         <div class="upsell_option" data-option-index="${idx}">
//           <div class="upsell_option_label">${escapeHtml(name)}</div>
//           <div class="upsell_option_values">
//             ${values.map(rawVal => {
//               const label = escapeHtml(rawVal);
//               const encoded = encodeURIComponent(rawVal);

//               if (isColor) {
//                 const Value_ = rawVal.toLowerCase().replace(/\s+/g, '');
//                 const swatchImg_ = `https://myntlab.com/cdn/shop/files/${Value_}fabricswatch.jpg?width=60`;

//                 return `
//                   <button type="button"
//                     class="upsell_opt_btn is-swatch-img"
//                     data-value="${encoded}"
//                     title="${label}"
//                     style="background-image:url('${swatchImg_}')">
//                     <span class="visually-hidden">${label}</span>
//                   </button>
//                 `;
//               }

//               return `
//                 <button type="button"
//                   class="upsell_opt_btn"
//                   data-value="${encoded}">${label}</button>
//               `;
//             }).join('')}
//           </div>
//         </div>
//       `);
//     });

//     if (!optsEl.children.length) {
//       const edit = card.querySelector('.custom_upsell_edit');
//       if (edit) edit.style.display = 'none';
//     }
//   }

//   /* -------------------------------------------
//      AUTO SELECT FIRST AVAILABLE VARIANT
//   ------------------------------------------- */
//   function autoSelectFirstAvailable(card) {
//     const handle = card.dataset.handle;
//     const p = productMap.get(handle);
//     if (!p) return;

//     const variants = p.variants || [];
//     const addBtn = card.querySelector('.custom_upsell_add');
//     const priceEl = card.querySelector('.custom_upsell_price');
//     const selectedEl = card.querySelector('.custom_upsell_selected');

//     const firstAvail = variants.find(v => v.available) || variants[0];
//     if (!firstAvail) return;

//     if (variants.length === 1) {
//       addBtn.disabled = !firstAvail.available;
//       addBtn.dataset.variantId = firstAvail.id;
//       if (priceEl) priceEl.textContent = formatMoney(firstAvail.price);
//       setCardImage(card, p, firstAvail);
//       if (selectedEl) selectedEl.textContent = firstAvail.available ? 'Ready to add' : 'Sold out';
//       return;
//     }

//     const selected = {};
//     if (firstAvail.option1) selected.option1 = firstAvail.option1;
//     if (firstAvail.option2) selected.option2 = firstAvail.option2;
//     if (firstAvail.option3) selected.option3 = firstAvail.option3;

//     card.dataset.selected = JSON.stringify(selected);

//     Object.keys(selected).forEach(k => {
//       const idx = Number(k.replace('option', ''));
//       const val = selected[k];

//       const wrap = card.querySelector(`.upsell_option[data-option-index="${idx}"]`);
//       if (!wrap) return;

//       wrap.querySelectorAll('.upsell_opt_btn').forEach(b => b.classList.remove('is-active'));

//       const btn = [...wrap.querySelectorAll('.upsell_opt_btn')]
//         .find(b => decodeURIComponent(b.dataset.value || '') === val);

//       if (btn) btn.classList.add('is-active');
//     });

//     updateVariantForCard(card);
//   }

//   /* -------------------------------------------
//      INTERACTIONS
//   ------------------------------------------- */
//   function attachInteractions(wrapper) {
//     if (wrapper.dataset.upsellsBound === "1") return;
//     wrapper.dataset.upsellsBound = "1";

//     wrapper.addEventListener('click', async (e) => {
//       const edit = e.target.closest('.custom_upsell_edit');
//       if (edit) {
//         const card = edit.closest('.custom_upsell_card');
//         if (!card) return;

//         card.classList.toggle('is-editing');
//         edit.textContent = card.classList.contains('is-editing') ? 'Close' : 'Edit';
//         return;
//       }

//       const optBtn = e.target.closest('.upsell_opt_btn');
//       if (optBtn) {
//         const card = optBtn.closest('.custom_upsell_card');
//         const optWrap = optBtn.closest('.upsell_option');
//         if (!card || !optWrap) return;

//         optWrap.querySelectorAll('.upsell_opt_btn').forEach(b => b.classList.remove('is-active'));
//         optBtn.classList.add('is-active');

//         const idx = Number(optWrap.dataset.optionIndex);
//         const rawVal = decodeURIComponent(optBtn.dataset.value || '');

//         const selected = safeParse(card.dataset.selected, {});
//         selected[`option${idx}`] = rawVal;
//         card.dataset.selected = JSON.stringify(selected);

//         updateVariantForCard(card);
//         return;
//       }

//       const addBtn = e.target.closest('.custom_upsell_add');
//       if (addBtn) {
//         const variantId = addBtn.dataset.variantId;
//         if (!variantId) return;

//         addBtn.disabled = true;

//         try {
//           await fetch('/cart/add.js', {
//             method: 'POST',
//             credentials: 'same-origin',
//             headers: { 'Content-Type': 'application/json' },
//             body: JSON.stringify({ id: Number(variantId), quantity: 1 })
//           });

//           await refreshCartUI();
//         } catch (err) {
//           console.error('Upsell add error:', err);
//         } finally {
//           addBtn.disabled = false;
//         }

//         return;
//       }
//     }, true);
//   }

//   /* -------------------------------------------
//      REFRESH CART UI AFTER UPSELL ADD
//   ------------------------------------------- */
//   async function refreshCartUI() {
//     try {
//       await refreshCartSections();
//     } catch (e) {
//       console.warn('Cart sections refresh fallback:', e);
//     }

//     document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
//     document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
//     window.dispatchEvent(new CustomEvent('cart:refresh'));
//     window.dispatchEvent(new CustomEvent('cart:updated'));

//     setTimeout(() => {
//       initExistingWrappers();
//     }, 120);
//   }

//   async function refreshCartSections() {
//     const sectionEls = [...document.querySelectorAll('[id^="shopify-section-"]')];
//     const sectionIds = [...new Set(
//       sectionEls
//         .map(el => el.id.replace('shopify-section-', ''))
//         .filter(id => /cart|drawer|bubble|icon/i.test(id))
//     )];

//     if (!sectionIds.length) return;

//     const url = `${window.location.pathname}?sections=${encodeURIComponent(sectionIds.join(','))}`;
//     const res = await fetch(url, { credentials: 'same-origin' });
//     const htmlBySection = await res.json();

//     sectionIds.forEach(sectionId => {
//       const mount = document.getElementById(`shopify-section-${sectionId}`);
//       const newHtml = htmlBySection[sectionId];

//       if (mount && newHtml) {
//         mount.innerHTML = newHtml;
//       }
//     });
//   }

//   /* -------------------------------------------
//      VARIANT MATCH + PRICE + IMAGE UPDATE
//   ------------------------------------------- */
//   function updateVariantForCard(card) {
//     const handle = card.dataset.handle;
//     const p = productMap.get(handle);
//     if (!p) return;

//     const variants = p.variants || [];
//     const selected = safeParse(card.dataset.selected, {});
//     const addBtn = card.querySelector('.custom_upsell_add');
//     const priceEl = card.querySelector('.custom_upsell_price');
//     const selectedEl = card.querySelector('.custom_upsell_selected');

//     if (variants.length === 1) {
//       addBtn.disabled = !variants[0].available;
//       addBtn.dataset.variantId = variants[0].id;
//       if (priceEl) priceEl.textContent = formatMoney(variants[0].price);
//       setCardImage(card, p, variants[0]);
//       if (selectedEl) selectedEl.textContent = variants[0].available ? 'Ready to add' : 'Sold out';
//       return;
//     }

//     const needed = [1,2,3].filter(i => variants.some(v => v[`option${i}`])).map(i => `option${i}`);
//     const missing = needed.filter(k => !selected[k]);

//     if (missing.length) {
//       addBtn.disabled = true;
//       addBtn.dataset.variantId = '';
//       if (selectedEl) selectedEl.textContent = 'Select options';
//       return;
//     }

//     const match = variants.find(v => needed.every(k =>
//       String(v[k]).toLowerCase() === String(selected[k]).toLowerCase()
//     ));

//     if (!match) {
//       addBtn.disabled = true;
//       addBtn.dataset.variantId = '';
//       if (selectedEl) selectedEl.textContent = 'Unavailable';
//       return;
//     }

//     addBtn.dataset.variantId = match.id;
//     if (priceEl) priceEl.textContent = formatMoney(match.price);
//     setCardImage(card, p, match);

//     if (!match.available) {
//       addBtn.disabled = true;
//       if (selectedEl) selectedEl.textContent = 'Sold out';
//       return;
//     }

//     addBtn.disabled = false;
//     if (selectedEl) selectedEl.textContent = needed.map(k => selected[k]).join(' / ');
//   }

//   function setCardImage(card, product, variant) {
//     const imgTag = card.querySelector('.custom_upsell_img_tag');
//     if (!imgTag) return;

//     const src =
//       variant?.featured_image?.src ||
//       variant?.featured_image ||
//       variant?.image ||
//       product?.featured_image ||
//       product?.images?.[0] ||
//       '';

//     if (src) imgTag.src = src;
//   }

//   /* -------------------------------------------
//      HELPERS
//   ------------------------------------------- */
//   function normalizeOptionNames(p) {
//     const o = p.options || [];
//     if (Array.isArray(o) && o.length && typeof o[0] === 'string') return o;
//     if (Array.isArray(o) && o.length && typeof o[0] === 'object') return o.map(x => x.name).filter(Boolean);
//     return [];
//   }

//   function safeParse(json, fallback) {
//     try { return JSON.parse(json); }
//     catch (e) { return fallback; }
//   }

//   function formatMoney(cents) {
//     const n = Number(cents);
//     const currency = (window.Shopify?.currency?.active) ? Shopify.currency.active : 'AUD';
//     try {
//       return (n / 100).toLocaleString(undefined, { style: 'currency', currency });
//     } catch (e) {
//       return '$' + (n / 100).toFixed(2);
//     }
//   }

//   function escapeHtml(str) {
//     return String(str).replace(/[&<>"']/g, m => ({
//       '&': '&amp;',
//       '<': '&lt;',
//       '>': '&gt;',
//       '"': '&quot;',
//       "'": '&#39;'
//     }[m]));
//   }

//   if (document.readyState === 'loading') {
//     document.addEventListener('DOMContentLoaded', boot);
//   } else {
//     boot();
//   }
// })();